import { useTranslation } from '@/i18n/client';
import cn from 'clsx';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
// 组件
import UKDialog from '@/components/UKDialog';
import { DialogActions, DialogTitle } from '@mui/material';
// 组件
import UKToast from '@/components/UKToast/index';
import UKTruncatedTooltip from '@/components/UKTruncatedTooltip';
import CDNImage from '@/components/cdnImage/cdnImage';
import JDIPrice from '@/components/price/index';
import CommonButton from '../commonButton';

import {
  LocalInfoDTO,
  OrderStatusVo,
  PointVO,
  PriceInfoItemDTOListVo,
  PriceInfoVo,
} from '@/apis/order/order.interface';
import styles from './index.module.scss';
// 图标
import TipIcon from '@/assets/icons/TipIcon.svg';
import VectorDown from '@/assets/icons/VectorDownBig.svg';
import JumpIcon from '@/assets/icons/jump_icon.svg';
// 埋点
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/25344';

interface orderDetailAmountProps {
  orderInfo: {
    detailInfo:
      | {
          label: string;
          value: string;
          customerName?: string;
          mobile?: string;
          isExpand?: boolean;
          showCopyButton?: boolean;
          copyButtonTitle?: string;
          jumpUrl?: string;
        }[]
      | null;
    priceInfoDTO: PriceInfoVo;
    orderStatusDTO: OrderStatusVo;
    orderId: string;
    localInfo?: LocalInfoDTO;
    pointVO?: PointVO;
  };
}

const orderDetailAmount: React.FC<orderDetailAmountProps> = observer(({ orderInfo }) => {
  const { t } = useTranslation();
  const { detailInfo, priceInfoDTO, orderStatusDTO, orderId, localInfo, pointVO } = orderInfo || {};
  const { price, savePrice, message, subTitle } = priceInfoDTO || {};
  const { orderStatusId } = orderStatusDTO || {};
  const priceInfoItemList = priceInfoDTO?.priceInfoItemDTOList || [];
  const [isItemViisible, setIsItemViisible] = useState(false);
  const [curPriceItem, setCurPriceItem] = useState<PriceInfoItemDTOListVo | null>(null);
  const [showTipModal, setShowTipModal] = useState(false);

  const commonTrackParams = {
    orderId: String(orderId),
    orderStatus: orderStatusId as '1' | '3' | '8' | '6' | '5',
  };

  // 展开与收起功能
  const toggleExpand = () => {
    setIsItemViisible(!isItemViisible);
  };

  // 复制订单号
  const copyHandler = () => {
    // 埋点
    sendClickTrack.Orderdetail_DetailCopy(commonTrackParams);
    UKToast.show({
      content: t('order.orderDetail.common.copySuccessTips'), // 弹窗内容
      icon: 'success', // 可选：success, fail, loading, warn, info，影响图标
      duration: 2000, // 不写的话默认3000
    });
  };

  // 跳转链接
  const handleJumpUrl = (url?: string) => {
    if (url) {
      // 判断是否为合法URL（以http://或https://开头，或者以/开头的相对路径）
      if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) {
        window.location.href = url;
      } else {
        console.error('Invalid URL format:', url);
      }
    }
  };

  // 非金额信息楼层
  const mainInfoRender = () => {
    const dom = (
      <>
        {detailInfo?.length
          ? detailInfo.map((item, index) => {
              return item?.customerName ? (
                <div className={styles.info_item_special} key={index}>
                  <span className={styles.info_item_label}>{item.label}</span>
                  <div className={styles.address_item}>
                    {item.isExpand ? (
                      <>
                        <div onClick={toggleExpand} className={styles.expand_wrap}>
                          <VectorDown
                            className={cn(styles.more_ico, {
                              [styles.retract_ico]: isItemViisible,
                            })}
                          />
                        </div>
                        {isItemViisible ? (
                          <>
                            <div className={styles.address_item_inner}>
                              <UKTruncatedTooltip
                                text={item?.customerName}
                                tooltipStyles={styles.address_nick_name}
                                tooltipProps={{
                                  placement: 'top',
                                  arrow: true,
                                }}
                              />
                              {item?.mobile ? (
                                <UKTruncatedTooltip
                                  text={item?.mobile || ''}
                                  tooltipStyles={styles.address_postcode}
                                  tooltipProps={{
                                    placement: 'top',
                                    arrow: true,
                                  }}
                                />
                              ) : null}
                            </div>
                            <UKTruncatedTooltip
                              text={item.value}
                              tooltipStyles={styles.address_detail}
                              tooltipProps={{
                                placement: 'top',
                                arrow: true,
                              }}
                            />
                          </>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <div className={styles.address_item_inner}>
                          <UKTruncatedTooltip
                            text={item?.customerName}
                            tooltipStyles={styles.address_nick_name}
                            tooltipProps={{
                              placement: 'top',
                              arrow: true,
                            }}
                          />
                          {item?.mobile ? (
                            <UKTruncatedTooltip
                              text={item?.mobile || ''}
                              tooltipStyles={styles.address_postcode}
                              tooltipProps={{
                                placement: 'top',
                                arrow: true,
                              }}
                            />
                          ) : null}
                        </div>
                        <UKTruncatedTooltip
                          text={item.value}
                          tooltipStyles={styles.address_detail}
                          tooltipProps={{
                            placement: 'top',
                            arrow: true,
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              ) : item.value ? (
                <div className={styles.info_item} key={index}>
                  <span className={styles.info_item_label}>{item.label}</span>
                  <span
                    className={cn(styles.info_item_value, {
                      [styles.info_item_value_jump]: item.jumpUrl,
                    })}
                    {...(item.jumpUrl && { onClick: () => handleJumpUrl(item.jumpUrl) })}
                  >
                    {item.value}
                    {item.showCopyButton && (
                      <CopyToClipboard text={item.value} onCopy={() => copyHandler()}>
                        <span
                          className={styles.copy_btn}
                          exptag={'exp|' + EXPOSURE_RECORD.Orderdetail_DetailCopyExpo}
                          data-exptag-config={JSON.stringify({
                            useClick: false,
                            stay_duration: 0.3,
                            repeated: false,
                            area_rate: 0.5,
                          })}
                          data-exptag-json-param={JSON.stringify(commonTrackParams)}
                        >
                          {item.copyButtonTitle}
                        </span>
                      </CopyToClipboard>
                    )}
                    {item.jumpUrl && (
                      <span className={styles.jump_icon_wrap}>
                        <JumpIcon />
                      </span>
                    )}
                  </span>
                </div>
              ) : null;
            })
          : null}
      </>
    );
    return dom;
  };

  // 订单金额明细渲染
  const amountItemRender = (item: PriceInfoItemDTOListVo) => {
    let itemDom = null;

    // 展示类型 1正，2:负，3:FREE 4:会员标
    if (item.priceDisplayType === 4) {
      // 会员身份下单展示方式
      itemDom = (
        <div className={styles.memberPriceStyle}>
          {item.originalPrice && (
            <JDIPrice
              symbol={localInfo?.currencySymbol || ''}
              price={item.originalPrice || ''}
              uiType={2}
              containerStyle={{
                color: '#888B94',
                marginRight: '8px',
                fontWeight: 500,
              }}
              lineThrough
            />
          )}
          {item.price ? <span className={styles.free_tip_style}>{item.price}</span> : null}
          {item.icon ? (
            <CDNImage
              src={item.icon}
              width={40}
              height={18}
              alt='icon'
              style={{ objectFit: 'contain', height: '18px', marginLeft: '8px' }}
            />
          ) : null}
        </div>
      );
    } else if (item.priceDisplayType === 3 && item.price) {
      // 特殊样式 价格展示free
      itemDom = (
        <div>
          {item.originalPrice && (
            <JDIPrice
              symbol={localInfo?.currencySymbol || ''}
              price={item.originalPrice || ''}
              uiType={2}
              containerStyle={{
                color: '#888B94',
                marginRight: '8px',
                fontWeight: 500,
              }}
              lineThrough
            />
          )}
          <span className={styles.free_tip_style}>{item.price}</span>
        </div>
      );
    } else if (item.priceDisplayType === 2 && item.price) {
      // 展示负号
      itemDom = (
        <>
          {item.zf}
          <JDIPrice
            symbol={localInfo?.currencySymbol || ''}
            price={item.price}
            uiType={2}
            containerStyle={{
              color: '#1A1A1A',
              fontWeight: 500,
            }}
          />
        </>
      );
    } else {
      itemDom = item.price ? (
        <JDIPrice
          symbol={localInfo?.currencySymbol || ''}
          price={item.price}
          uiType={2}
          containerStyle={{
            color: '#1A1A1A',
            fontWeight: 500,
          }}
        />
      ) : null;
    }
    return itemDom;
  };

  // 金额信息楼层
  const amountInfoRender = () => {
    const dom = (
      <>
        <div className={cn(styles.info_item, styles.info_item_large)}>
          <span className={cn(styles.info_item_label, styles.info_item_label2)}>
            <span className={styles.boldStyle}>{message}</span>&nbsp;{subTitle}
          </span>
          <div className={styles.right_amt}>
            {/* 节省金额暂不展示 */}
            {/* {Number(savePrice) ? (
              <span className={styles.save_price}>
                {t("order.orderDetail.orderDetailAmount.saveText")}{" "}
                <JDIPrice
                  symbol={localInfo?.currencySymbol || ""}
                  price={savePrice || ""}
                  uiType={2}
                  containerStyle={{
                    fontSize: "14px",
                    color: "#CC0C1C",
                  }}
                />
              </span>
            ) : null} */}
            {price ? (
              <span className={cn(styles.price_font, styles.price_large)}>
                <JDIPrice
                  symbol={localInfo?.currencySymbol || ''}
                  price={price || ''}
                  uiType={2}
                  containerStyle={{
                    color: '#1A1A1A',
                    fontSize: '18px',
                  }}
                />
              </span>
            ) : null}
          </div>
        </div>
        {priceInfoItemList?.length
          ? priceInfoItemList.map((item, index) => {
              return item.type === 2 ? (
                <div className={styles.info_item_divide} key={index}></div>
              ) : (
                <>
                  <div className={styles.info_item} key={index}>
                    {/* 拼接展示Subtotal(x items) */}
                    <div
                      className={cn(styles.info_item_label, styles.info_item_label2, {
                        [styles.infoItemClickStyle]: item.tipCopyTitle,
                      })}
                      onClick={() => {
                        if (item.tipCopyTitle) {
                          setCurPriceItem(item);
                          setShowTipModal(true);
                        }
                      }}
                    >
                      {item.title} {item.subTitle}
                      {item.tipCopyTitle ? (
                        <div className={styles.tipIconStyle}>
                          <TipIcon width={14} height={14} />
                        </div>
                      ) : null}
                    </div>
                    <div className={styles.price_font}>{amountItemRender(item)}</div>
                  </div>
                  {/* 子金额明细列表 */}
                  {!!item?.subItems?.length &&
                    item?.subItems?.map((ele, ind) => {
                      return (
                        <div
                          className={cn(styles.info_item, styles.childPriceWrap)}
                          key={`childPrice${ind}`}
                        >
                          {/* 拼接展示Subtotal(x items) */}
                          <div
                            className={cn(styles.info_item_label, styles.info_item_label2, {
                              [styles.infoItemClickStyle]: ele.tipCopyTitle,
                            })}
                            onClick={() => {
                              if (ele.tipCopyTitle) {
                                setCurPriceItem(ele);
                                setShowTipModal(true);
                              }
                            }}
                          >
                            {ele.title} {ele.subTitle}
                            {ele.tipCopyTitle ? (
                              <div className={styles.tipIconStyle}>
                                <TipIcon width={14} height={14} />
                              </div>
                            ) : null}
                          </div>
                          <div className={styles.price_font}>{amountItemRender(ele)}</div>
                        </div>
                      );
                    })}
                </>
              );
            })
          : null}

        {(pointVO?.showPointRedeemed || pointVO?.showPointReward) && (
          <div className={styles.pointsWrap}>
            {/* 积分抵扣楼层 */}
            {pointVO?.showPointRedeemed && (
              <div className={styles.info_item}>
                <div className={cn(styles.info_item_label, styles.info_item_label2)}>
                  {pointVO?.redeemedTitle}
                </div>
                {pointVO?.redeemed && pointVO?.amountDeducted ? (
                  <div className={styles.redeemedContent}>
                    {pointVO?.redeemed} = -{pointVO?.amountDeductedUnit}
                    {pointVO?.amountDeducted}
                  </div>
                ) : null}
              </div>
            )}
            {/* 积分获取楼层 */}
            {pointVO?.showPointReward && (
              <div className={styles.info_item}>
                <div className={cn(styles.info_item_label, styles.info_item_label2)}>
                  {pointVO?.rewardTitle}
                  {pointVO?.rewardIcon ? (
                    <CDNImage
                      src={pointVO?.rewardIcon || ''}
                      width={28}
                      height={14}
                      alt='icon'
                      style={{ objectFit: 'contain', height: '14px', marginLeft: '4px' }}
                    />
                  ) : null}
                </div>
                <div className={styles.rewardContent}>{pointVO?.reward}</div>
              </div>
            )}
          </div>
        )}
      </>
    );
    return dom;
  };
  return (
    <div className={styles.wrap}>
      <div className={styles.left_info}>{mainInfoRender()}</div>
      <div className={styles.right_info}>{amountInfoRender()}</div>
      {/* 费项解释弹框 */}
      <UKDialog
        open={showTipModal}
        onClose={() => setShowTipModal(false)} // 关闭弹窗
        showCloseBtn={true}
        sx={{
          '& .MuiDialog-paper': {
            width: '366px', // 设置自定义宽度
            minWidth: '366px',
          },
        }}
      >
        <div>
          <DialogTitle
            sx={{
              paddingTop: '20px',
              paddingBottom: '0',
            }}
          >
            {curPriceItem?.tipCopyTitle || ''}
          </DialogTitle>
          <div className={styles.tipsContentStyle}>{curPriceItem?.tipCopy || ''}</div>
          <DialogActions
            sx={{
              paddingTop: '0',
            }}
          >
            <CommonButton variant='contained' onClick={() => setShowTipModal(false)} sx={{ boxShadow: 'none' }}>
              {t('common.btnText.ok')}
            </CommonButton>
          </DialogActions>
        </div>
      </UKDialog>
    </div>
  );
});

export default orderDetailAmount;
